import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ArrowLeft, Settings as SettingsIcon, Bell, Lock, User, Palette, HelpCircle, Eye, EyeOff, ExternalLink, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocationScope } from '../context/LocationContext';
import { supabase } from '../lib/supabase';

const NOTIFICATION_TOGGLES = [
  { key: 'push_new_follower', label: 'New followers' },
  { key: 'push_friend_request', label: 'Follow requests' },
  { key: 'push_rating_comment', label: 'Comments on your ratings' },
  { key: 'push_bar_comment', label: 'Comments on bars you\'ve rated' },
  { key: 'push_friend_rating', label: 'Friends rate a bar' },
  { key: 'push_bar_request_approved', label: 'Bar request approved' },
  { key: 'push_bar_request_rejected', label: 'Bar request rejected' },
  { key: 'push_check_in', label: 'Friends check in at a bar' },
];

function Settings() {
  const navigate = useNavigate();
  const { user, profile, updateProfile } = useAuth();
  const { enabledMetros, hometownMetro, setHometown } = useLocationScope();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [notifPrefs, setNotifPrefs] = useState(null);

  // Fetch notification preferences
  useEffect(() => {
    if (!user) return;

    const fetchPrefs = async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // No row exists yet — create one with defaults
        const { data: newRow } = await supabase
          .from('notification_preferences')
          .insert({ user_id: user.id })
          .select()
          .single();
        setNotifPrefs(newRow);
      } else if (data) {
        setNotifPrefs(data);
      }
    };

    fetchPrefs();
  }, [user]);

  const updateNotifPref = useCallback(async (key, value) => {
    if (!user || !notifPrefs) return;

    // Optimistic update
    setNotifPrefs(prev => ({ ...prev, [key]: value }));

    const { error } = await supabase
      .from('notification_preferences')
      .update({ [key]: value, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (error) {
      // Revert on failure
      setNotifPrefs(prev => ({ ...prev, [key]: !value }));
      setError('Failed to update notification preference');
      setTimeout(() => setError(''), 3000);
    }
  }, [user, notifPrefs]);

  const openIOSSettings = () => {
    if (Capacitor.isNativePlatform()) {
      window.open('app-settings:', '_system');
    }
  };

  // Redirect to auth if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black p-6">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="text-center py-12">
            <SettingsIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h1 className="text-2xl font-display text-white mb-2">Settings</h1>
            <p className="text-gray-400 mb-6">Please sign in to access settings</p>
            <Link
              to="/auth"
              className="btn-primary inline-block"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handlePrivacyToggle = async () => {
    const newPrivacy = profile?.profile_privacy === 'private' ? 'public' : 'private';
    try {
      await updateProfile({ profile_privacy: newPrivacy });
      setSuccess(`Account is now ${newPrivacy}`);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update privacy');
      setSuccess('');
    }
  };

  const isPrivate = profile?.profile_privacy === 'private';
  const masterEnabled = notifPrefs?.push_enabled ?? true;

  return (
    <div className="min-h-screen bg-dive-black p-6">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-display text-white">Settings</h1>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Account Section */}
        <div className="mb-6">
          <h2 className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-3 px-1">
            Account
          </h2>
          <div className="bg-dive-dark rounded-xl border border-dive-gray overflow-hidden divide-y divide-dive-gray">
            {/* Edit Profile */}
            <Link
              to="/profile"
              className="flex items-center gap-4 p-4 hover:bg-dive-gray/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-dive-gray flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Edit Profile</p>
                <p className="text-gray-500 text-sm">Update your name, bio, and photo</p>
              </div>
            </Link>

            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-dive-gray flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-dive-neon" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">Hometown</p>
                  <p className="text-gray-500 text-sm">Map and list open here when GPS is off</p>
                </div>
              </div>
              <select
                value={hometownMetro?.id || 'chicago'}
                onChange={async (e) => {
                  try {
                    await setHometown(e.target.value);
                    setSuccess('Hometown updated');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err) {
                    setError(err.message || 'Failed to update hometown');
                  }
                }}
                className="bg-dive-darker border border-dive-gray rounded-lg px-2 py-2 text-sm text-white max-w-[140px]"
              >
                {enabledMetros.map((metro) => (
                  <option key={metro.id} value={metro.id}>
                    {metro.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Privacy Toggle */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isPrivate ? 'bg-purple-500/20' : 'bg-dive-gray'
                }`}>
                  {isPrivate ? (
                    <EyeOff className="w-5 h-5 text-purple-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Private Account</p>
                  <p className="text-gray-500 text-sm">
                    {isPrivate
                      ? 'Only approved followers can see your activity'
                      : 'Anyone can see your ratings and follow you'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={handlePrivacyToggle}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                  isPrivate ? 'bg-purple-500' : 'bg-dive-gray'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                  isPrivate ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="mb-6">
          <h2 className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-3 px-1">
            Notifications
          </h2>
          <div className="bg-dive-dark rounded-xl border border-dive-gray overflow-hidden divide-y divide-dive-gray">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  masterEnabled ? 'bg-dive-amber/20' : 'bg-dive-gray'
                }`}>
                  <Bell className={`w-5 h-5 ${masterEnabled ? 'text-dive-amber' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Enable push notifications</p>
                  <p className="text-gray-500 text-sm">Master toggle for all push notifications</p>
                </div>
              </div>
              <button
                onClick={() => updateNotifPref('push_enabled', !masterEnabled)}
                disabled={!notifPrefs}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                  masterEnabled ? 'bg-dive-amber' : 'bg-dive-gray'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                  masterEnabled ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>

            {/* Individual Toggles */}
            {NOTIFICATION_TOGGLES.map(({ key, label }) => {
              const enabled = notifPrefs?.[key] ?? true;
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-4 pl-18 ${
                    !masterEnabled ? 'opacity-40 pointer-events-none' : ''
                  }`}
                >
                  <p className="text-white text-sm pl-14">{label}</p>
                  <button
                    onClick={() => updateNotifPref(key, !enabled)}
                    disabled={!notifPrefs || !masterEnabled}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      enabled && masterEnabled ? 'bg-dive-amber' : 'bg-dive-gray'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                      enabled && masterEnabled ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
              );
            })}

            {/* iOS Settings Link */}
            {Capacitor.isNativePlatform() && (
              <button
                onClick={openIOSSettings}
                className="flex items-center justify-center gap-2 p-3 w-full text-gray-500 text-sm hover:text-gray-400 transition-colors"
              >
                Manage in iOS Settings
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Preferences Section */}
        <div className="mb-6">
          <h2 className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-3 px-1">
            Preferences
          </h2>
          <div className="bg-dive-dark rounded-xl border border-dive-gray overflow-hidden divide-y divide-dive-gray">
            <div className="flex items-center gap-4 p-4 opacity-60">
              <div className="w-10 h-10 rounded-lg bg-dive-gray flex items-center justify-center flex-shrink-0">
                <Palette className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium flex items-center gap-2">
                  Appearance
                  <span className="text-[10px] bg-dive-amber/20 text-dive-amber px-2 py-0.5 rounded-full">
                    Coming Soon
                  </span>
                </p>
                <p className="text-gray-500 text-sm">Theme and display options</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="mb-6">
          <h2 className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-3 px-1">
            Support
          </h2>
          <div className="bg-dive-dark rounded-xl border border-dive-gray overflow-hidden">
            <div className="flex items-center gap-4 p-4 opacity-60">
              <div className="w-10 h-10 rounded-lg bg-dive-gray flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium flex items-center gap-2">
                  Help Center
                  <span className="text-[10px] bg-dive-amber/20 text-dive-amber px-2 py-0.5 rounded-full">
                    Coming Soon
                  </span>
                </p>
                <p className="text-gray-500 text-sm">FAQs and support</p>
              </div>
            </div>
          </div>
        </div>

        {/* App Version */}
        <p className="text-center text-gray-600 text-sm mt-8">
          Tilted v1.0.0
        </p>
      </div>
    </div>
  );
}

export default Settings;
